import { Component, type ErrorInfo, type ReactNode } from 'react';
import { shouldShowErrorDetailsFromApiUser } from '../../util/show-error-details';

type Props = { children: ReactNode };

type State = { hasError: boolean; error: Error | null };

/**
 * Catches synchronous React errors outside of React Router’s `errorElement` tree
 * (e.g. providers above `RouterProvider`).
 */
export class AppErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, error: null };

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('[AppErrorBoundary]', error, info.componentStack);
    }

    render(): ReactNode {
        if (this.state.hasError && this.state.error) {
            const { message, stack } = this.state.error;
            const showDetails = shouldShowErrorDetailsFromApiUser();
            const showStack = Boolean(showDetails && stack);

            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: 24,
                        fontFamily: 'system-ui, sans-serif',
                        background: 'radial-gradient(#fff, #eee)'
                    }}
                >
                    <div style={{ maxWidth: 480 }}>
                        <h1 style={{ fontSize: '1.25rem', marginBottom: 12 }}>Something went wrong</h1>
                        {showDetails ? (
                            <p style={{ color: '#333', marginBottom: 16 }}>{message}</p>
                        ) : (
                            <p style={{ color: '#555', marginBottom: 16 }}>
                                Please try again. If the problem continues, contact support.
                            </p>
                        )}
                        {showStack ? (
                            <pre
                                style={{
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    background: '#f5f5f5',
                                    padding: 8,
                                    borderRadius: 4,
                                    marginBottom: 16
                                }}
                            >
                                {stack}
                            </pre>
                        ) : null}
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '8px 16px',
                                fontSize: '1rem',
                                cursor: 'pointer'
                            }}
                        >
                            Reload page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
